import Dictionary from "@/app/lib/dictionary/dictionary";

export function devLog(...args: any[]) {
    if (process.env.NODE_ENV === 'development') {
        console.log(...args);
    }
}

export function isAlphaChar(char: string): boolean {
    if (char === '' || char === undefined) {
        return false;
    }
    const charCode: number = char.charCodeAt(0);
    return (charCode >= 65 && charCode <= 90) || (charCode >= 97 && charCode <= 122);
}

export function isSingleAlphabeticChar(string: string): boolean {
    return /^[A-Za-z]$/.test(string);
}

export function isUppercaseLetter(char: string): boolean {
    return /^[A-Z]$/.test(char);
}

export function firstChar(string: string): string {
    if (string === '') {
        return '';
    }

    return string[0];
}

export function clamp(number: number, min: number, max: number): number {
    return Math.min(Math.max(number, min), max);
}

export function clamp01(number: number): number {
    return clamp(number, 0, 1);
}

export function validateWord(word: string, dictionary: Dictionary) {
    return dictionary.wordSet.has(word);
}

export function getRandom<T>(array: Array<T>): T {
    const randomIndex: number = randomInteger(0, array.length);
    return array[randomIndex];
}

export function getRandomSubset<T>(count: number, array: Array<T>): Array<T> {
    
    // safety check, we can't get a subset bigger than the set
    if (count > array.length) {
        count = array.length;
    }
    if (count < 0) {
        count = 0;
    }

    // make a new array we can play with
    const superArray = array.slice();
    const subArray = new Array<T>();

    for (let i = 0; i < count; i++) {
        const randomIndex: number = randomInteger(0, superArray.length);
        const [randomEntry] : Array<T> = superArray.splice(randomIndex, 1);
        subArray.push(randomEntry);
    }

    return subArray;
}

export function randomInteger(min: number, max: number, inclusive: boolean = false) {
    min = Math.ceil(min);
    max = Math.floor(max);

    if (inclusive) {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    } else {
        return Math.floor(Math.random() * (max - min)) + min;
    }
}

export function weightedRandom<T>(items: { choice: T; weight: number}[]): T {
    // cleanup to only positive weights
    const cleanItems = items.map(item => ({
        ...item,
        weight: Math.max(item.weight, 0)
    }));
    // get the total
    let totalWeight: number = 0;
    for (const item of cleanItems) {
        totalWeight += item.weight;
    }

    // case for when all weights are 0
    if (totalWeight === 0) {
        return getRandom(items).choice;
    }

    // iterate to a random position
    const randomPosition = Math.random() * totalWeight;
    let currentPosition = 0;
    for (const item of cleanItems) {
        currentPosition += item.weight;
        if (randomPosition < currentPosition) {
            return item.choice;
        }
    }
    // fallback for compiler safety
    return items[items.length - 1].choice;
}

export function binomialSimulator(trials: number, probability: number): number {
    const p = clamp01(probability);
    let successes: number = 0;
    for (let i = 0; i < trials; i++) {
        if (Math.random() <= p) {
            successes++;
        }
    }
    return successes;
}

// converts a decimal number to an integer stochastically
// so 1.25 has 25% chance of being 1, 75% chance of being 2
export function stochasticInt(value: number) : number {
    if (Number.isInteger(value)) {
        return value;
    }
    const decimal: number = value - Math.floor(value);
    if (Math.random() < decimal) {
        return Math.floor(value);
    }
    else {
        return Math.ceil(value);
    }
}

export function shuffle<T>(array: Array<T>): Array<T> {
    // make a shallow copy so the original array isn't modified
    const result = [...array];

    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
}

// NOTE: Only works for simple, un-nested arrays
export function compareSimpleArrays<T>(
    a: T[],
    b: T[],
    orderMatters: boolean = true
) : boolean {
    if (!orderMatters) {
        const mapA = new Map<T, number>();
        const mapB = new Map<T, number>();

        for (const item of a) {
            const count: number = mapA.get(item) ?? 0;
            mapA.set(item, count + 1);
        }
        for (const item of b) {
            const count: number = mapB.get(item) ?? 0;
            mapB.set(item, count + 1);
        }

        return compareSimpleMaps<T, number>(mapA, mapB);
    }

    if (a.length !== b.length)
        return false;

    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) {
            return false;
        }
    }
    return true;
}

export function compareSimpleMaps<T, U>(a: Map<T, U>, b: Map<T, U>) : boolean {
    if (a.size !== b.size)    
        return false;

    // we checked size already so we whould only have to iterate over one map
    for (const [key, value] of a.entries()) {
        if (value !== b.get(key))
            return false;
    }
    return true;
}

export function buildFrequencyMap<T>(array: T[]) : Map<T, number> {
    const frequencyMap = new Map<T, number>();
    for (const item of array) {
        frequencyMap.set(item, (frequencyMap.get(item) ?? 0) + 1)
    }
    return frequencyMap;
}